import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { getLeadsBookedReportRequest } from '../../Utils/RestUtils'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

const ListDailyTotals = () => {
  const [reportResponse, setReportResponse] = useState(null)
  const [isLoadingReport, setIsLoadingReport] = useState(false)

  const formatUkDateTime = (value) => {
    if (!value) return ''
    try {
      const date = new Date(value)
      return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date)
    } catch (e) {
      return String(value)
    }
  }

  const getInitialDates = () => {
    const today = new Date()
    const fromDate = new Date(
      Date.UTC(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        0,
        0,
        0,
        0
      )
    )

    const toDate = new Date(
      Date.UTC(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        23,
        59,
        59,
        999
      )
    )
    return {
      from: fromDate.toISOString(),
      to: toDate.toISOString(),
    }
  }

  const [filters, setFilters] = useState(getInitialDates())

  const getLeadsBooked = async () => {
    setIsLoadingReport(true)

    const response = await getLeadsBookedReportRequest(filters)
    setReportResponse(response.data)
    setIsLoadingReport(false)
  }

  return (
    <CardContent>
      <Typography typography="h4" component="h1" mb={2}>
        Daily Totals
      </Typography>
      <Box
        sx={{
          display: 'flex',
          marginTop: '10px',
          marginBottom: '10px',
          gap: '1rem',
        }}
      >
        <Box>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              inputFormat="DD/MM/YYYY"
              label="From"
              value={dayjs(filters.from)}
              onChange={(selectedDate) => {
                const date = selectedDate.toDate()
                const utcDate = new Date(
                  Date.UTC(
                    date.getFullYear(),
                    date.getMonth(),
                    date.getDate(),
                    0,
                    0,
                    0,
                    0
                  )
                )
                setFilters({
                  ...filters,
                  from: utcDate.toISOString(),
                })
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
        </Box>
        <Box>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              inputFormat="DD/MM/YYYY"
              maxDate={
                new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
              }
              label="To"
              value={dayjs(filters.to)}
              onChange={(selectedDate) => {
                const date = selectedDate.toDate()
                const utcDate = new Date(
                  Date.UTC(
                    date.getFullYear(),
                    date.getMonth(),
                    date.getDate(),
                    23,
                    59,
                    59,
                    999
                  )
                )
                setFilters({
                  ...filters,
                  to: utcDate.toISOString(),
                })
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
        </Box>
        <Box></Box>
        <Box>
          <Button
            disabled={isLoadingReport}
            onClick={() => {
              getLeadsBooked()
            }}
          >
            Search
          </Button>
        </Box>
      </Box>
      <Card style={{ padding: '20px' }}>
        {isLoadingReport ? (
          <Box sx={{ width: '100%' }}>
            <LinearProgress />
          </Box>
        ) : (
          reportResponse !== null &&
          reportResponse.totals.length !== 0 && (
            <React.Fragment>
              <Table>
                <caption
                  style={{
                    captionSide: 'top',
                    textAlign: 'left',
                    fontWeight: 600,
                    padding: '8px 0',
                  }}
                >
                  Totals & Avgs for time period before cancellations
                </caption>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: '15%' }}>Total leads</TableCell>
                    <TableCell style={{ width: '15%' }}>Day of week</TableCell>
                    <TableCell>Total Booked (avg.)</TableCell>
                    <TableCell>Total Scrap (avg.)</TableCell>
                    <TableCell>Total Shore Cars (aavg.)</TableCell>
                    <TableCell>Total BMS Salvage (avg.)</TableCell>
                    <TableCell>Total Salvage (avg.)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow key={reportResponse.totals.total_booked}>
                    <TableCell>{reportResponse.totals.total_leads}</TableCell>
                    <TableCell>&nbsp;</TableCell>
                    <TableCell>
                      {reportResponse.totals.total_booked} (
                      {reportResponse.totals.avg_booked})
                    </TableCell>
                    <TableCell>
                      {reportResponse.totals.total_scrap} (
                      {reportResponse.totals.avg_scrap})
                    </TableCell>
                    <TableCell>
                      {reportResponse.totals.total_shore_cars} (
                      {reportResponse.totals.avg_shore_cars})
                    </TableCell>
                    <TableCell>
                      {reportResponse.totals.total_bms_salvage} (
                      {reportResponse.totals.avg_bms_salvage})
                    </TableCell>
                    <TableCell>
                      {reportResponse.totals.total_salvage} (
                      {reportResponse.totals.avg_total_salvage})
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Table>
                <caption
                  style={{
                    captionSide: 'top',
                    textAlign: 'left',
                    fontWeight: 600,
                    padding: '16px 0 8px',
                  }}
                >
                  Daily breakdown of bookings before cancellations
                </caption>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: '15%' }}>Day of week</TableCell>
                    <TableCell style={{ width: '15%' }}>Date</TableCell>
                    <TableCell>Booked</TableCell>
                    <TableCell>Scrap</TableCell>
                    <TableCell>Shore Cars</TableCell>
                    <TableCell>BMS Salvage</TableCell>
                    <TableCell>Total Salvage</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportResponse?.days?.map((record) => (
                    <TableRow key={record.day}>
                      <TableCell>{record.day_name}</TableCell>
                      <TableCell>{formatUkDateTime(record.day)}</TableCell>
                      <TableCell>
                        {record.bms_salvage + record.shore_cars + record.scrap}
                      </TableCell>
                      <TableCell>{record.scrap}</TableCell>
                      <TableCell>{record.shore_cars}</TableCell>
                      <TableCell>{record.bms_salvage}</TableCell>
                      <TableCell>
                        {record.bms_salvage + record.shore_cars}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </React.Fragment>
          )
        )}
        {reportResponse && reportResponse.length === 0 ? (
          <Alert icon={false} severity="error">
            No results found.
          </Alert>
        ) : null}
        {reportResponse === null && (
          <Alert icon={false} severity="info">
            Search for results
          </Alert>
        )}
      </Card>
    </CardContent>
  )
}

export default ListDailyTotals
